import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { MorningBriefData } from '@/hooks/useRevenueIntelligence';
import { formatUiNumber, formatUiPercent } from '@/lib/ui-format';

interface MorningBriefProps {
  data: MorningBriefData;
}

const getUrgencyStyle = (
  urgency: MorningBriefData['priority_actions'][number]['urgency']
): string => {
  if (urgency === 'high') {
    return 'bg-status-danger/10 text-status-danger border-status-danger/30';
  }
  if (urgency === 'medium') {
    return 'bg-status-warning/10 text-status-warning border-status-warning/30';
  }
  return 'bg-muted text-muted-foreground border-border';
};

export const MorningBrief = ({ data }: MorningBriefProps): JSX.Element => {
  return (
    <Card className="h-full">
      <CardHeader className="space-y-2">
        <CardTitle className="text-lg">Morning Brief</CardTitle>
        <p className="text-sm text-muted-foreground">Daily snapshot with priorities</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-muted-foreground">Messages</p>
            <p className="font-semibold text-foreground">
              {formatUiNumber(data.metrics.messages_sent.value)}{' '}
              <span className="text-xs text-muted-foreground">
                ({data.metrics.messages_sent.delta >= 0 ? '+' : ''}
                {data.metrics.messages_sent.delta})
              </span>
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Reply Rate</p>
            <p className="font-semibold text-foreground">
              {formatUiPercent(data.metrics.reply_rate.value / 100, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              <span className="text-xs text-muted-foreground">
                {' '}
                ({data.metrics.reply_rate.delta >= 0 ? '+' : ''}
                {data.metrics.reply_rate.delta})
              </span>
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Meetings</p>
            <p className="font-semibold text-foreground">
              {formatUiNumber(data.metrics.meetings_booked.value)}{' '}
              <span className="text-xs text-muted-foreground">
                ({data.metrics.meetings_booked.delta >= 0 ? '+' : ''}
                {data.metrics.meetings_booked.delta})
              </span>
            </p>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">Priority Actions</p>
          {data.priority_actions.length === 0 && (
            <p className="text-sm text-muted-foreground">No urgent actions right now.</p>
          )}
          <div className="space-y-2">
            {data.priority_actions.map((action) => (
              <div
                key={`${action.type}-${action.contact_name}-${action.company}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{action.action_text}</p>
                  <p className="text-xs text-muted-foreground">
                    {action.contact_name} · {action.company}
                  </p>
                </div>
                <Badge variant="outline" className={getUrgencyStyle(action.urgency)}>
                  {action.urgency.toUpperCase()}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">Insights</p>
          <div className="space-y-2">
            {data.insights.map((insight) => (
              <div key={insight.title} className="rounded-lg border border-border p-3">
                <p className="text-sm font-medium text-foreground">{insight.title}</p>
                <p className="text-xs text-muted-foreground">{insight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
